import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, AnimatePresence } from 'motion/react';
import { ArrowRight, Briefcase, Users, Clock, Target, TrendingUp, CheckCircle, Award, Shield, RefreshCw, FileText, Calendar, Tag } from 'lucide-react';
import { stats, services, testimonials, industries } from '../mock';
import { newsletters } from '../newsletterData';
import SEO from '../components/SEO';

const Home = () => {
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 500], [0, 150]);
  const heroOpacity = useTransform(scrollY, [0, 300], [1, 0]);
  const [currentIndustryIndex, setCurrentIndustryIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndustryIndex((prev) => (prev + 1) % industries.length);
    }, 3000); // Change every 3 seconds
    return () => clearInterval(interval);
  }, []);

  const springConfig = {
    type: "spring",
    damping: 80,
    stiffness: 400,
    mass: 1
  };

  const iconMap = {
    briefcase: Briefcase,
    users: Users,
    clock: Clock,
    target: Target,
    'trending-up': TrendingUp,
    'file-text': FileText,
    award: Award,
    shield: Shield,
    'refresh-cw': RefreshCw
  };

  return (
    <div>
      <SEO 
        title="Home"
        description="Munoret - Your trusted partner in talent acquisition and workforce solutions. Expert executive search, permanent placement, and staffing services. 15+ years connecting top talent with leading organizations."
        keywords="recruitment agency, talent acquisition, executive search, permanent placement, temporary staffing, HR solutions, workforce management, Munoret"
      />
      {/* Hero Section */}
      <section className="gradient-hero" style={{ 
        minHeight: '90vh',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Animated Background Elements */}
        <motion.div
          style={{
            position: 'absolute',
            top: '10%',
            right: '10%',
            width: '300px',
            height: '300px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.1)',
            filter: 'blur(40px)',
          }}
          animate={{
            y: [0, 30, 0],
            x: [0, 20, 0],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          style={{
            position: 'absolute',
            bottom: '20%',
            left: '5%',
            width: '200px',
            height: '200px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.08)',
            filter: 'blur(30px)',
          }}
          animate={{
            y: [0, -20, 0],
            x: [0, -15, 0],
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />

        <motion.div 
          className="container" 
          style={{ 
            maxWidth: '1200px',
            textAlign: 'center',
            position: 'relative',
            zIndex: 2,
            padding: '80px 24px',
            y: heroY,
            opacity: heroOpacity
          }}
        >
          <motion.h1 
            className="display-hero"
            initial={{ opacity: 0, y: 150 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springConfig, delay: 0.1 }}
            style={{ 
              marginBottom: '24px',
              color: 'var(--text-primary)'
            }}
          >
            Transform Your Workforce
            <br />
            With Expert Talent Solutions
          </motion.h1>
          
          <motion.p 
            className="body-large"
            initial={{ opacity: 0, y: 150 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springConfig, delay: 0.2 }}
            style={{ 
              marginBottom: '40px',
              color: 'var(--text-secondary)',
              maxWidth: '800px',
              margin: '0 auto 40px'
            }}
          >
            From executive search to temporary staffing, we connect exceptional talent with forward-thinking organizations. With 15+ years of proven success, we also provide comprehensive staff augmentation, talent sourcing, and lifecycle management—empowering your existing HR teams with the support they need to optimize workforce performance.
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0, y: 150 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springConfig, delay: 0.3 }}
            style={{ 
              display: 'flex',
              gap: '16px',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}
          >
            <Link to="/careers" className="btn-primary" style={{ gap: '8px' }}>
              View Open Positions
              <ArrowRight size={20} />
            </Link>
            <Link to="/services" className="btn-secondary">
              Our Services
            </Link>
          </motion.div>
        </motion.div>
      </section>

      {/* Stats Section */}
      <section style={{ padding: '80px 0', background: 'var(--bg-primary)' }}>
        <div className="container" style={{ maxWidth: '1200px' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '48px',
            textAlign: 'center'
          }}>
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50, scale: 0.8 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true }}
                transition={{ 
                  delay: index * 0.1,
                  type: "spring",
                  damping: 20,
                  stiffness: 300
                }}
                whileHover={{ 
                  scale: 1.05,
                  transition: { duration: 0.2 }
                }}
              >
                <motion.div 
                  className="heading-large" 
                  style={{ 
                    color: 'var(--brand-primary)',
                    marginBottom: '8px'
                  }}
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ 
                    delay: index * 0.1 + 0.2,
                    type: "spring",
                    damping: 15,
                    stiffness: 200
                  }}
                >
                  {stat.value}
                </motion.div>
                <div style={{ color: 'var(--text-secondary)' }}>
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Slideshow Section */}
      <section style={{ padding: '80px 0', background: 'var(--bg-light)' }}>
        <div className="container" style={{ maxWidth: '1200px' }}>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center', marginBottom: '48px' }}
          >
            <h2 className="heading-large" style={{ marginBottom: '16px' }}>
              Top Industries We Serve
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              Specialized expertise across diverse sectors
            </p>
          </motion.div>

          {/* Animated Slideshow */}
          <div style={{
            position: 'relative',
            height: '120px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden'
          }}>
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndustryIndex}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -50 }}
                transition={{ duration: 0.5 }}
                style={{
                  position: 'absolute',
                  width: '100%',
                  textAlign: 'center'
                }}
              >
                <div 
                  className="heading-medium"
                  style={{ 
                    color: 'var(--brand-primary)',
                    padding: '0 24px'
                  }}
                >
                  {industries[currentIndustryIndex]}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Industry Grid - All Industries */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '24px',
            marginTop: '48px'
          }}>
            {industries.map((industry, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                whileHover={{ 
                  scale: 1.05,
                  boxShadow: '0 8px 20px rgba(0, 0, 0, 0.1)',
                  transition: { duration: 0.2 }
                }}
                style={{
                  background: 'white',
                  padding: '24px',
                  borderRadius: '12px',
                  border: '2px solid var(--border-medium)',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
              >
                <div style={{
                  fontSize: '16px',
                  fontWeight: '500',
                  color: 'var(--text-primary)'
                }}>
                  {industry}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section style={{ padding: '80px 0', background: 'var(--bg-light)' }}>
        <div className="container" style={{ maxWidth: '1200px' }}>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center', marginBottom: '64px' }}
          >
            <h2 className="heading-large" style={{ marginBottom: '16px' }}>
              Comprehensive Talent Solutions
            </h2>
            <p className="body-large" style={{ 
              color: 'var(--text-secondary)',
              maxWidth: '700px',
              margin: '0 auto'
            }}>
              Whether you need a C-suite executive or temporary staff, we deliver the right talent at the right time.
            </p>
          </motion.div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '32px'
          }}>
            {services.slice(0, 9).map((service, index) => {
              const IconComponent = iconMap[service.icon];
              return (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ 
                    y: -8,
                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
                    transition: { duration: 0.3 }
                  }}
                  style={{
                    background: 'white',
                    padding: '32px',
                    borderRadius: '16px',
                    border: '1px solid var(--border-medium)',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                >
                  <div style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '12px',
                    background: 'var(--bg-light)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '20px'
                  }}>
                    <IconComponent size={28} style={{ color: 'var(--brand-primary)' }} />
                  </div>
                  <h3 className="heading-medium" style={{ marginBottom: '12px' }}>
                    {service.title}
                  </h3>
                  <p style={{ 
                    color: 'var(--text-secondary)',
                    marginBottom: '20px',
                    lineHeight: '1.6'
                  }}>
                    {service.description}
                  </p>
                  <ul style={{ 
                    listStyle: 'none',
                    padding: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px'
                  }}>
                    {service.features.slice(0, 3).map((feature, idx) => (
                      <li key={idx} style={{ 
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        color: 'var(--text-secondary)',
                        fontSize: '14px'
                      }}>
                        <CheckCircle size={16} style={{ color: 'var(--brand-primary)', flexShrink: 0 }} />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>

          <div style={{ textAlign: 'center', marginTop: '48px' }}>
            <Link to="/services" className="btn-primary">
              View All Services
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section style={{ padding: '80px 0', background: 'var(--bg-primary)' }}>
        <div className="container" style={{ maxWidth: '1200px' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '48px',
            alignItems: 'center'
          }}>
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="heading-large" style={{ marginBottom: '24px' }}>
                Why Choose Munoret?
              </h2>
              <p className="body-large" style={{ 
                color: 'var(--text-secondary)',
                marginBottom: '32px',
                lineHeight: '1.6'
              }}>
                We're not just recruiters—we're your strategic talent partners committed to your long-term success.
              </p>
              <ul style={{ 
                listStyle: 'none',
                padding: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '16px'
              }}>
                {[
                  'Deep industry expertise across multiple sectors',
                  'Proprietary candidate vetting process',
                  'Average 21-day time-to-hire for critical roles',
                  '95% client satisfaction and retention rate',
                  'Dedicated account management team'
                ].map((item, idx) => (
                  <li key={idx} style={{ 
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px'
                  }}>
                    <CheckCircle size={24} style={{ 
                      color: 'var(--brand-primary)',
                      flexShrink: 0,
                      marginTop: '2px'
                    }} />
                    <span style={{ color: 'var(--text-primary)' }}>{item}</span>
                  </li>
                ))}
              </ul>
              <div style={{ marginTop: '32px' }}>
                <Link to="/about" className="btn-primary">
                  Learn More About Us
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              style={{
                background: 'var(--bg-light)',
                padding: '48px',
                borderRadius: '24px',
                border: '1px solid var(--border-medium)'
              }}
            >
              <h3 className="heading-medium" style={{ marginBottom: '24px' }}>
                Client Testimonial
              </h3>
              <p style={{ 
                color: 'var(--text-secondary)',
                marginBottom: '24px',
                fontSize: '18px',
                lineHeight: '1.6',
                fontStyle: 'italic'
              }}>
                "{testimonials[0].content}"
              </p>
              <div style={{ borderTop: '1px solid var(--border-medium)', paddingTop: '16px' }}>
                <div style={{ fontWeight: '500', color: 'var(--text-primary)' }}>
                  {testimonials[0].name}
                </div>
                <div style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
                  {testimonials[0].title}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Industry Newsletters Section */}
      <section style={{ padding: '80px 0', background: 'var(--bg-primary)' }}>
        <div className="container" style={{ maxWidth: '1200px' }}>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center', marginBottom: '64px' }}
          >
            <h2 className="heading-large" style={{ marginBottom: '16px' }}>
              Latest Industry Insights
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              Stay informed with our expert analysis and workforce trends
            </p>
          </motion.div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '32px',
            marginBottom: '48px'
          }}>
            {newsletters.slice(0, 3).map((newsletter, index) => (
              <motion.article
                key={newsletter.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ 
                  y: -8,
                  boxShadow: '0 12px 24px rgba(0, 0, 0, 0.1)',
                  transition: { duration: 0.2 }
                }}
                style={{
                  background: 'white',
                  borderRadius: '16px',
                  border: '1px solid var(--border-medium)',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
              >
                <div style={{
                  background: 'var(--bg-light)',
                  padding: '20px',
                  borderBottom: '1px solid var(--border-medium)'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '12px',
                    flexWrap: 'wrap'
                  }}>
                    <span style={{
                      background: 'var(--brand-primary)',
                      color: 'white',
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}>
                      {newsletter.category}
                    </span>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      color: 'var(--text-muted)',
                      fontSize: '13px'
                    }}>
                      <Calendar size={14} />
                      {newsletter.date}
                    </div>
                  </div>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: 'var(--text-primary)',
                    lineHeight: '1.4',
                    marginBottom: '8px'
                  }}>
                    {newsletter.title}
                  </h3>
                  <div style={{
                    fontSize: '13px',
                    color: 'var(--text-secondary)',
                    fontWeight: '500'
                  }}>
                    {newsletter.industry}
                  </div>
                </div>
                
                <div style={{
                  padding: '20px',
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  <p style={{
                    color: 'var(--text-secondary)',
                    fontSize: '14px',
                    lineHeight: '1.6',
                    marginBottom: '16px',
                    flex: 1
                  }}>
                    {newsletter.excerpt.substring(0, 120)}...
                  </p>
                </div>
              </motion.article>
            ))}
          </div>

          <div style={{ textAlign: 'center' }}>
            <Link to="/newsletters" className="btn-primary">
              View All Newsletters
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="gradient-subtle" style={{ padding: '80px 0' }}>
        <div className="container" style={{ maxWidth: '800px', textAlign: 'center' }}>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="heading-large" style={{ marginBottom: '24px' }}>
              Ready to Find Your Perfect Talent Match?
            </h2>
            <p className="body-large" style={{ 
              color: 'var(--text-secondary)',
              marginBottom: '40px'
            }}>
              Let's discuss your staffing needs and how we can help you build a world-class team.
            </p>
            <div style={{ 
              display: 'flex',
              gap: '16px',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <Link to="/contact" className="btn-primary" style={{ gap: '8px' }}>
                Get Started Today
                <ArrowRight size={20} />
              </Link>
              <Link to="/careers" className="btn-secondary">
                Browse Jobs
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
